import api from '../config/api';

/**
 * COGS Service
 * Handles all API calls related to Product COGS management
 */
class CogsService {
  /**
   * Fetch COGS for a brand with optional filters
   * @param {number} brandId - Brand ID
   * @param {object} params - Query parameters (sku, warehouse_code, active_only, include_history, limit, offset)
   * @returns {Promise} COGS data array
   */
  async getCogs(brandId, params = {}) {
    try {
      const response = await api.get(`/api/cogs/brands/${brandId}/cogs`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching COGS:', error);
      throw error;
    }
  }

  /**
   * Fetch the PO-date -> goods-receipt-date pairs behind a SKU's computed lead time.
   * Powers the hover tooltip on the Lead Time column.
   * @param {number} brandId - Brand ID
   * @param {string} sku - Product SKU
   * @returns {Promise} { median_lead_time_days, sample_size, data: [{po_number, po_date, received_date, lead_days, ...}] }
   */
  async getLeadTimeHistory(brandId, sku) {
    try {
      const response = await api.get(`/api/cogs/brands/${brandId}/cogs/${encodeURIComponent(sku)}/lead-time-history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching lead-time history:', error);
      throw error;
    }
  }

  /**
   * Set a human override for a computed lead-time metric.
   * @param {number} brandId - Brand ID
   * @param {object} args - { sku, metric ('lead_time_restock'|'lead_time_production'), value (days), note?, effective_from? }
   * @returns {Promise} { success, id, sku, metric, value }
   */
  async setLeadTimeOverride(brandId, { sku, metric, value, note, effective_from }) {
    try {
      const response = await api.post(`/api/cogs/brands/${brandId}/overrides`, { sku, metric, value, note, effective_from });
      return response.data;
    } catch (error) {
      console.error('Error setting lead-time override:', error);
      throw error;
    }
  }

  /**
   * Clear (close) the active human override for a lead-time metric — the computed value takes over again.
   * @param {number} brandId - Brand ID
   * @param {object} args - { sku, metric ('lead_time_restock'|'lead_time_production') }
   * @returns {Promise} { success, cleared, sku, metric }
   */
  async clearLeadTimeOverride(brandId, { sku, metric }) {
    try {
      // axios DELETE carries a body under the `data` key.
      const response = await api.delete(`/api/cogs/brands/${brandId}/overrides`, { data: { sku, metric } });
      return response.data;
    } catch (error) {
      console.error('Error clearing lead-time override:', error);
      throw error;
    }
  }

  /**
   * Create or update COGS (single or bulk)
   * @param {number} brandId - Brand ID
   * @param {array|object} cogsData - COGS data (single object or array for bulk)
   * @returns {Promise} Created/updated COGS data
   */
  async createOrUpdateCogs(brandId, cogsData) {
    try {
      const response = await api.post(`/api/cogs/brands/${brandId}/cogs`, {
        cogs: Array.isArray(cogsData) ? cogsData : [cogsData]
      });
      return response.data;
    } catch (error) {
      console.error('Error creating/updating COGS:', error);
      throw error;
    }
  }

  /**
   * Update COGS for a specific SKU (creates new historical entry)
   * @param {number} brandId - Brand ID
   * @param {string} sku - Product SKU
   * @param {object} cogsData - Updated COGS data
   * @returns {Promise} Updated COGS data
   */
  async updateCogs(brandId, sku, cogsData) {
    try {
      const response = await api.put(`/api/cogs/brands/${brandId}/cogs/${encodeURIComponent(sku)}`, cogsData);
      return response.data;
    } catch (error) {
      console.error('Error updating COGS:', error);
      throw error;
    }
  }

  /**
   * Delete (soft delete) COGS for a specific SKU
   * @param {number} brandId - Brand ID
   * @param {string} sku - Product SKU
   * @param {string} warehouseCode - Optional warehouse code. Omitted (the
   *   product-level delete) closes the SKU's cost rows at ALL warehouses and
   *   deactivates the product; pass a code to close just that warehouse's row.
   * @returns {Promise} Success message
   */
  async deleteCogs(brandId, sku, warehouseCode = null) {
    try {
      const response = await api.delete(`/api/cogs/brands/${brandId}/cogs/${encodeURIComponent(sku)}`, {
        params: warehouseCode ? { warehouse_code: warehouseCode } : {}
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting COGS:', error);
      throw error;
    }
  }

  /**
   * Get COGS history for a specific SKU
   * @param {number} brandId - Brand ID
   * @param {string} sku - Product SKU
   * @param {string} warehouseCode - Warehouse code (optional, default: 'default')
   * @returns {Promise} Historical COGS data array
   */
  async getCogsHistory(brandId, sku, warehouseCode = 'default') {
    try {
      const response = await api.get(`/api/cogs/brands/${brandId}/cogs/${encodeURIComponent(sku)}/history`, {
        params: { warehouse_code: warehouseCode }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching COGS history:', error);
      throw error;
    }
  }

  /**
   * Get primary SKUs (L0/L1) being sold without COGS entries
   * @param {number} brandId - Brand ID
   * @param {object} params - Query parameters (days_back, min_order_count, limit, offset)
   * @returns {Promise} Missing COGS data array
   */
  async getMissingCOGS(brandId, params = {}) {
    try {
      const response = await api.get(`/api/cogs/brands/${brandId}/missing-cogs`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching missing COGS:', error);
      throw error;
    }
  }

  /**
   * Get SKUs with warehouse-specific COGS gaps
   * @param {number} brandId - Brand ID
   * @param {object} params - Query parameters (days_back, min_order_count, limit, offset)
   * @returns {Promise} Warehouse COGS gaps data array
   */
  async getWarehouseCogsGaps(brandId, params = {}) {
    try {
      const response = await api.get(`/api/cogs/brands/${brandId}/warehouse-cogs-gaps`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouse COGS gaps:', error);
      throw error;
    }
  }

  /**
   * Get bundles (L2) being sold without component mappings
   * @param {number} brandId - Brand ID
   * @param {object} params - Query parameters (days_back, min_order_count, limit, offset)
   * @returns {Promise} Missing bundle mappings data array
   */
  async getMissingBundleMappings(brandId, params = {}) {
    try {
      const response = await api.get(`/api/cogs/brands/${brandId}/missing-bundle-mappings`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching missing bundle mappings:', error);
      throw error;
    }
  }

  /**
   * Create a bundle with components
   * @param {number} brandId - Brand ID
   * @param {object} bundleData - Bundle data with components
   * @returns {Promise} Created bundle data
   */
  async createBundle(brandId, bundleData) {
    try {
      const response = await api.post(`/api/cogs/brands/${brandId}/bundles`, bundleData);
      return response.data;
    } catch (error) {
      console.error('Error creating bundle:', error);
      throw error;
    }
  }

  /**
   * Bulk import COGS from CSV
   * @param {number} brandId - Brand ID
   * @param {File} file - CSV file
   * @returns {Promise} Import result with success/error counts
   */
  async bulkImportCogs(brandId, file) {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`/api/cogs/brands/${brandId}/cogs/bulk-import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error bulk importing COGS:', error);
      throw error;
    }
  }

  /**
   * Download CSV template for bulk import
   * Template demonstrates 3-level BOM hierarchy:
   * - Level 0 (Parts): Raw materials/components used to make products
   * - Level 1 (SKUs): Finished products made from parts
   * - Level 2 (Bundles): Kits/packs containing multiple Level 1 products
   * @returns {string} CSV template string
   */
  getCSVTemplate() {
    return `sku,warehouse_code,product_name,cogs_type,sku_type,bom_level,cogs,currency,bundle_overhead,warehouse_shipping_cost,inbound_freight_cost,effective_from,notes,gtin,asin
FABRIC-001,default,Premium Cotton Fabric (1 yard),simple,raw_material,0,5.00,USD,0.00,0.00,0.00,2024-01-01,Raw material - used to make shirts,,
BUTTON-SET-001,default,Button Set (10 pieces),simple,raw_material,0,0.50,USD,0.00,0.00,0.00,2024-01-01,Raw material - used to make shirts,,
THREAD-001,default,Polyester Thread (100m spool),simple,raw_material,0,0.25,USD,0.00,0.00,0.00,2024-01-01,Raw material - used to make shirts,,
SHIRT-BLACK-M,default,Black T-Shirt Medium,simple,finished_good,1,5.75,USD,0.00,1.50,0.50,2024-01-01,Made from FABRIC-001 + BUTTON-SET-001 + THREAD-001,0123456789012,B08EXAMPLE1
SHIRT-BLACK-L,default,Black T-Shirt Large,simple,finished_good,1,6.00,USD,0.00,1.50,0.50,2024-01-01,Made from FABRIC-001 + BUTTON-SET-001 + THREAD-001,0123456789013,B08EXAMPLE2
SHIRT-3PACK-BLK,default,Black T-Shirt 3-Pack (M/L/XL),bundle,finished_good,2,19.50,USD,2.00,2.00,1.00,2024-01-01,Bundle of 3 shirts with packaging overhead,0123456789014,B08EXAMPLE3`;
  }

  /**
   * Download current COGS as CSV
   * @param {number} brandId - Brand ID
   * @returns {Promise<Blob>} CSV file blob
   */
  async downloadCogsCSV(brandId) {
    try {
      const cogsData = await this.getCogs(brandId, { limit: 10000 });

      // Backend returns "data", not "cogs"
      const cogsArray = cogsData.data || cogsData.cogs || [];
      if (!cogsArray || cogsArray.length === 0) {
        throw new Error('No COGS data to export');
      }

      // Create CSV header
      const headers = [
        'sku', 'warehouse_code', 'product_name', 'cogs_type', 'sku_type', 'bom_level',
        'cogs', 'currency', 'bundle_overhead', 'effective_from', 'effective_to',
        'notes', 'warehouse_shipping_cost', 'inbound_freight_cost', 'gtin', 'asin'
      ];

      // Create CSV rows
      const rows = cogsArray.map(item => {
        return headers.map(header => {
          const value = item[header] || '';
          // Escape values with commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',');
      });

      // Combine header and rows
      const csv = [headers.join(','), ...rows].join('\n');

      // Create blob
      return new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    } catch (error) {
      console.error('Error downloading COGS CSV:', error);
      throw error;
    }
  }

  /**
   * Bulk-import COGS from already-parsed rows (e.g. from BulkImportDialog).
   * Re-stringifies as CSV to match the existing /bulk-import endpoint shape.
   * Returns { inserted, failed, errors } shape expected by BulkImportDialog.
   */
  async bulkImportCogsRows(brandId, rows, warehouseCode = 'default') {
    if (!Array.isArray(rows) || rows.length === 0) {
      return { inserted: 0, failed: 0, errors: [] };
    }
    // Union of keys across all rows so partial rows still produce a valid CSV.
    const headerSet = new Set();
    for (const r of rows) for (const k of Object.keys(r)) headerSet.add(k);
    const headers = Array.from(headerSet);
    const esc = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [
      headers.join(','),
      ...rows.map(r => headers.map(h => esc(r[h])).join(',')),
    ].join('\n');

    try {
      const response = await api.post(
        `/api/cogs/brands/${brandId}/cogs/bulk-import`,
        { csv_data: csv, warehouse_code: warehouseCode }
      );
      // Existing endpoint returns { success_count, error_count, errors } —
      // adapt to BulkImportDialog's expected shape.
      const d = response.data || {};
      return {
        inserted: d.success_count ?? d.inserted ?? 0,
        failed: d.error_count ?? d.failed ?? 0,
        errors: d.errors || [],
        // W20b: rows whose cost was not written (e.g. ambiguous master) — shown as an import warning.
        cogs_skipped: Array.isArray(d.cogs_skipped) ? d.cogs_skipped : [],
      };
    } catch (error) {
      console.error('Error bulk-importing COGS rows:', error);
      throw error;
    }
  }

  /**
   * Trigger a CSV download for the brand's current COGS. Uses the existing
   * downloadCogsCSV which builds the blob in-memory.
   */
  async exportCogsCsv(brandId) {
    const blob = await this.downloadCogsCSV(brandId);
    const filename = `cogs-${brandId}-${new Date().toISOString().slice(0, 10)}.csv`;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return { success: true, filename };
  }

  /**
   * Get available SKUs with COGS for use as bundle components
   * @param {number} brandId - Brand ID
   * @returns {Promise} Array of SKUs with COGS that can be used as components
   */
  async getAvailableComponents(brandId) {
    try {
      const response = await api.get(`/api/cogs/brands/${brandId}/available-components`);
      return response.data;
    } catch (error) {
      console.error('Error fetching available components:', error);
      throw error;
    }
  }

  /**
   * Get warehouses where a SKU has been shipped + existing COGS per warehouse
   * @param {number} brandId - Brand ID
   * @param {string} sku - Product SKU
   * @param {number} daysBack - Days back to look for orders (default: 365)
   * @returns {Promise} Array of warehouses with order counts and existing COGS
   */
  async getSkuWarehouses(brandId, sku, daysBack = 365) {
    try {
      const response = await api.get(`/api/cogs/brands/${brandId}/sku-warehouses/${encodeURIComponent(sku)}`, {
        params: { days_back: daysBack }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching SKU warehouses:', error);
      throw error;
    }
  }
}

export const cogsService = new CogsService();
export default cogsService;
